import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listTaskTemplates,
  createTaskTemplate,
  updateTaskTemplate,
  deleteTaskTemplate,
  applyTaskTemplate,
  type TaskTemplateInput,
} from '../lib/taskTemplatesApi';

// CLE-DASH — task templates. Read by the Settings tab and the Create Project
// modal. Applying a template creates tasks, so it invalidates the task queries.

export const TASK_TEMPLATES_KEY = ['task-templates'] as const;

export function useTaskTemplates() {
  return useQuery({
    queryKey: TASK_TEMPLATES_KEY,
    queryFn: listTaskTemplates,
    staleTime: 5 * 60 * 1000,
  });
}

function useInvalidateTemplates() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: TASK_TEMPLATES_KEY });
  };
}

export function useCreateTaskTemplate() {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: (input: TaskTemplateInput) => createTaskTemplate(input),
    onSuccess: invalidate,
  });
}

export function useUpdateTaskTemplate() {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TaskTemplateInput }) =>
      updateTaskTemplate(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteTaskTemplate() {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: (id: string) => deleteTaskTemplate(id),
    onSuccess: invalidate,
  });
}

export function useApplyTaskTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, projectId }: { id: string; projectId: string }) =>
      applyTaskTemplate(id, projectId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['task-estimate-totals'] });
    },
  });
}
